import { useAuth } from '@/src/context/AuthContext';
import { useSubscription } from '@/src/context/SubscriptionContext';
import { useTheme } from '@/src/context/ThemeContext';
import { supabase } from '@/src/core/config/supabase';
import { useImageUpload } from '@/src/presentation/hooks/useImageUpload';
import { SearchUsersUseCase } from '@/src/domain/usecases/SearchUsersUseCase';
import {
  addLeagueParticipantUseCase,
  completeLeagueUseCase,
  createLeagueTournamentUseCase,
  deleteLeagueUseCase,
  getLeagueByIdUseCase,
  getLeagueParticipantsUseCase,
  getLeagueStandingsUseCase,
  removeLeagueParticipantUseCase,
  updateLeagueUseCase,
  sendNotificationUseCase,
} from '@/src/core/dependencies';
import { League, LeagueParticipant, LeagueStanding, ModerationError, Tournament, TournamentType } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { LeagueDetailScreen } from '@/src/presentation/screens/LeagueDetailScreen';
import { logger } from '@/src/core/utils';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { getPlanLimitMessageKey } from '@/src/core/utils/planLimitErrors';
import { useFocusEffect } from '@react-navigation/native';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import React, { useCallback, useState } from 'react';
import { ActivityIndicator, Text, View } from 'react-native';

const searchUsersUseCase = new SearchUsersUseCase();

export default function LeagueDetailContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { checkAccess } = useSubscription();
  const { id } = useLocalSearchParams<{ id: string }>();

  const [league, setLeague] = useState<League | null>(null);
  const [participants, setParticipants] = useState<LeagueParticipant[]>([]);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [standings, setStandings] = useState<LeagueStanding[]>([]);
  const [loading, setLoading] = useState(true);
  const [creatingTournament, setCreatingTournament] = useState(false);
  const [updatingLeague, setUpdatingLeague] = useState(false);

  const { pickAndUploadImage: pickLogo, uploading: uploadingLogo } = useImageUpload({
    bucket: 'league-logos',
    userId: user?.id || '',
    preset: 'avatar',
    fieldType: 'avatar_image',
    onSuccess: (url) => handleUpdateLogo(url),
  });

  const handleUpdateLogo = async (url: string) => {
    if (!id) return;
    try {
      setUpdatingLeague(true);
      const updated = await updateLeagueUseCase.execute(id, { logoUrl: url }, user?.id);
      setLeague(updated);
    } catch (error) {
      logger.error('LeagueDetail', 'Failed to update logo', { error });
    } finally {
      setUpdatingLeague(false);
    }
  };

  useFocusEffect(
    useCallback(() => {
      loadLeagueData();
    }, [id])
  );

  const loadLeagueData = async () => {
    if (!id) return;

    try {
      setLoading(true);

      const leagueData = await getLeagueByIdUseCase.execute(id);
      if (!leagueData) {
        platformAlert(t('common.error'), t('leagues.leagueNotFound'));
        router.back();
        return;
      }

      setLeague(leagueData);

      const participantsData = await getLeagueParticipantsUseCase.execute(id);
      setParticipants(participantsData);

      // Load league tournaments
      const { data: tournamentData, error } = await supabase
        .from('tournaments')
        .select('*')
        .eq('league_id', id)
        .order('created_at', { ascending: true });

      if (!error && tournamentData) {
        const mapped = tournamentData.map((t: any) => ({
          id: t.id,
          name: t.name,
          description: t.description || undefined,
          tournamentType: t.tournament_type,
          status: t.status,
          maxParticipants: t.max_participants || undefined,
          userId: t.user_id,
          isPublic: t.is_public,
          createdAt: new Date(t.created_at),
          updatedAt: new Date(t.updated_at),
          leagueId: t.league_id || undefined,
        }));
        setTournaments(mapped);
      }

      // Load standings
      try {
        const standingsData = await getLeagueStandingsUseCase.execute(id);
        setStandings(standingsData);
      } catch (e) {
        logger.error('LeagueDetail', 'Failed to load standings', { error: e });
        setStandings([]);
      }
    } catch (error) {
      logger.error('LeagueDetail', 'Failed to load league', { error });
      platformAlert(t('common.error'), t('leagues.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddParticipant = async (name: string, userId?: string) => {
    if (!id) return;

    try {
      const participant = await addLeagueParticipantUseCase.execute({
        leagueId: id,
        name,
        userId,
      });
      setParticipants((prev) => [...prev, participant]);

      // Send league invitation notification
      if (userId) {
        try {
          await sendNotificationUseCase.execute(
            userId,
            'league_invitation',
            t('notifications.leagueInvitation'),
            t('notifications.leagueInvitationBody', { name: league?.name || '' }),
            { leagueId: id }
          );
        } catch {}
      }

      platformAlert(t('common.success'), t('alerts.leagueParticipantAdded'));
    } catch (error) {
      if (error instanceof ModerationError) {
        platformAlert(t('common.error'), t('moderation.contentBlocked'));
      } else {
        logger.error('LeagueDetail', 'Failed to add participant', { error });
        platformAlert(t('common.error'), error instanceof Error ? error.message : t('errors.failedToLoad'));
      }
    }
  };

  const handleRemoveParticipant = async (participantId: string) => {
    if (!id) return;

    platformAlert(
      t('tournaments.removeParticipant'),
      t('tournaments.confirmRemoveParticipant'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await removeLeagueParticipantUseCase.execute(participantId, id);
              setParticipants((prev) => prev.filter((p) => p.id !== participantId));
              platformAlert(t('common.success'), t('alerts.leagueParticipantRemoved'));
            } catch (error) {
              logger.error('LeagueDetail', 'Failed to remove participant', { error });
              platformAlert(t('common.error'), error instanceof Error ? error.message : t('errors.failedToLoad'));
            }
          },
        },
      ]
    );
  };

  const showPlanLimitAlert = (messageKey: string) => {
    platformAlert(t('subscription.featureLocked'), t(messageKey), [
      { text: t('common.cancel'), style: 'cancel' },
      { text: t('subscription.upgradeToPro'), onPress: () => router.push('/paywall') },
    ]);
  };

  // Same limits the check_tournament_plan_limits trigger enforces, checked up front
  // so free users get the paywall instead of a raw database error.
  const checkLeagueTournamentAccess = async (type: TournamentType): Promise<boolean> => {
    const typeAccess = await checkAccess({ feature: 'tournament_type', tournamentType: type });
    if (!typeAccess.allowed) {
      showPlanLimitAlert('subscription.tournamentTypeProOnly');
      return false;
    }

    const createAccess = await checkAccess({ feature: 'create_tournament' });
    if (!createAccess.allowed) {
      showPlanLimitAlert('subscription.tournamentLimitReached');
      return false;
    }

    const activeInLeague = tournaments.filter(
      (tournament) => tournament.status === 'setup' || tournament.status === 'in_progress'
    ).length;
    const leagueAccess = await checkAccess({ feature: 'league_tournament', currentCount: activeInLeague });
    if (!leagueAccess.allowed) {
      showPlanLimitAlert('subscription.leagueTournamentLimitReached');
      return false;
    }

    // League tournaments inherit the league visibility (CreateLeagueTournamentUseCase)
    if (!league?.isPublic) {
      const privateAccess = await checkAccess({ feature: 'private_tournament' });
      if (!privateAccess.allowed) {
        showPlanLimitAlert('subscription.privateTournamentLimitReached');
        return false;
      }
    }

    return true;
  };

  const handleCreateTournament = async (name: string, type: TournamentType) => {
    if (!id || !user) return;

    if (!(await checkLeagueTournamentAccess(type))) return;

    try {
      setCreatingTournament(true);
      const tournament = await createLeagueTournamentUseCase.execute({
        leagueId: id,
        name,
        tournamentType: type,
        userId: user.id,
      });

      // Update league status if it changed
      if (league?.status === 'setup') {
        setLeague((prev) => prev ? { ...prev, status: 'active' } : null);
      }

      platformAlert(t('common.success'), t('alerts.tournamentCreatedInLeague'));
      router.push(`/tournament/${tournament.id}`);
    } catch (error) {
      const planLimitKey = getPlanLimitMessageKey(error);
      if (error instanceof ModerationError) {
        platformAlert(t('common.error'), t('moderation.contentBlocked'));
      } else if (planLimitKey) {
        // Plan limit enforced by the DB trigger (e.g. subscription changed meanwhile) — expected
        logger.warn('LeagueDetail', 'Tournament creation blocked by plan limit', { error });
        showPlanLimitAlert(planLimitKey);
      } else {
        logger.error('LeagueDetail', 'Failed to create tournament', { error });
        platformAlert(t('common.error'), error instanceof Error ? error.message : t('leagues.failedToCreate'));
      }
    } finally {
      setCreatingTournament(false);
    }
  };

  const handleTournamentPress = (tournamentId: string) => {
    router.push(`/tournament/${tournamentId}`);
  };

  const handleUpdateLeague = async (updates: Partial<League>) => {
    if (!id) return;

    try {
      setUpdatingLeague(true);
      const updated = await updateLeagueUseCase.execute(id, updates, user?.id);
      setLeague(updated);
      platformAlert(t('common.success'), t('leagues.leagueUpdated'));
    } catch (error) {
      if (error instanceof ModerationError) {
        platformAlert(t('common.error'), t('moderation.contentBlocked'));
      } else {
        logger.error('LeagueDetail', 'Failed to update league', { error });
        platformAlert(t('common.error'), error instanceof Error ? error.message : t('errors.failedToLoad'));
      }
    } finally {
      setUpdatingLeague(false);
    }
  };

  const handleCompleteLeague = async () => {
    if (!id) return;

    platformAlert(
      t('leagues.completeLeague'),
      t('leagues.confirmCompleteLeague'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.confirm'),
          onPress: async () => {
            try {
              const updated = await completeLeagueUseCase.execute(id);
              setLeague(updated);
              platformAlert(t('common.success'), t('leagues.leagueCompleted'));
            } catch (error) {
              logger.error('LeagueDetail', 'Failed to complete league', { error });
              platformAlert(t('common.error'), error instanceof Error ? error.message : t('leagues.notAllTournamentsCompleted'));
            }
          },
        },
      ]
    );
  };

  const handleDeleteLeague = () => {
    if (!id) return;

    platformAlert(
      t('leagues.deleteLeague'),
      t('leagues.confirmDeleteLeague'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteLeagueUseCase.execute(id);
              platformAlert(t('common.success'), t('leagues.leagueDeleted'));
              router.replace('/leagues');
            } catch (error) {
              logger.error('LeagueDetail', 'Failed to delete league', { error });
              platformAlert(t('common.error'), t('leagues.failedToDelete'));
            }
          },
        },
      ]
    );
  };

  const handleSearchUser = async (query: string): Promise<Array<{ id: string; email: string; name?: string }>> => {
    try {
      const results = await searchUsersUseCase.execute(query);
      return results.map((u) => ({ id: u.id, email: u.email, name: u.name }));
    } catch (error) {
      logger.error('LeagueDetail', 'Failed to search user', { error });
      return [];
    }
  };

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.background }}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (!league) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.background }}>
        <Text style={{ color: colors.error, fontSize: 18 }}>{t('leagues.leagueNotFound')}</Text>
      </View>
    );
  }

  const isOwner = !!user && user.id === league.userId;

  return (
    <>
      <Stack.Screen options={{ title: league.name }} />
      <LeagueDetailScreen
        league={league}
        participants={participants}
        tournaments={tournaments}
        standings={standings}
        isOwner={isOwner}
        onAddParticipant={handleAddParticipant}
        onRemoveParticipant={handleRemoveParticipant}
        onCreateTournament={handleCreateTournament}
        onTournamentPress={handleTournamentPress}
        creatingTournament={creatingTournament}
        onUpdateLeague={handleUpdateLeague}
        onCompleteLeague={handleCompleteLeague}
        onDeleteLeague={handleDeleteLeague}
        updatingLeague={updatingLeague}
        onUploadLogo={pickLogo}
        uploadingLogo={uploadingLogo}
        onSearchUser={handleSearchUser}
        onNavigateToProfile={(userId) => router.push(`/profile/${userId}`)}
        colors={colors}
      />
    </>
  );
}
